import { faChevronDown, faChevronUp, faPerson, faRefresh, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { EXPLAIN_DDX, SCAN_MULTI } from "../../../amplify/api-gateway/write";
import CopyToClipboardButton from "../../assistant/CopyToClipboardButton";
import { FakeLoadingBar } from "../../dashboard/llm/common/FakeLoadingBar";
import { LLMDashboardFeedbackBar } from "../../dashboard/llm/common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../../dashboard/llm/common/LLMDashboardUpdated";
import { isSectionUnread, processTextForCopy } from "../../dashboard/llm/utils";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { LLMDashboardMarkdownRenderer } from "../../dashboard/llm/common/LLMDashboardMarkdownRenderer";
import { getLocalDateStrFromDate, getLocalTimeStrFromDate } from "../../../utils/time";

export const SectionTrackboardVerticalVideo = ({
    user,
    tenant,
    className='',
    isLoadingDdx,
    ddx,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    handleShowScanMultiModal,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [scanMulti, setScanMulti] = useState();

    useEffect(() => {
        // It's important to load the version of the file that the explain data
        // was generated from to ensure the citations line up
        if (patientVisit && latestFileVersions && latestFileVersions[EXPLAIN_DDX]) {
            const version = latestFileVersions[EXPLAIN_DDX];

            dashboardLLMCache.get(SCAN_MULTI, patientVisit, tenant, version.source_version_id)
                .then((data: any) => {
                    setScanMulti(data);
                });
        }
    }, [latestFileVersions]);

    if (!isLoadingDdx && (!ddx || ddx["error"])) {
        return (
            <></>
        )
    }

    const getTextToCopy = (type: 'vertical' | 'video') => {
        let anchorDate = new Date();
        if (latestFileVersions && latestFileVersions[EXPLAIN_DDX]) {
            anchorDate = new Date(latestFileVersions[EXPLAIN_DDX].version_date);
        }

        const status = type === 'vertical' ? ddx.suitable_vertical : ddx.suitable_video;
        const justification = type === 'vertical' ? ddx.suitable_vertical_justification : ddx.suitable_video_justification;
        const label = type === 'vertical' ? 'Vertical' : 'Video';

        return `${label} Eligibility as of ${getLocalDateStrFromDate(anchorDate)} ${getLocalTimeStrFromDate(anchorDate)}:\n` +
               `Status: ${status}\n` +
               `Justification: ${processTextForCopy(justification, anchorDate)}`;
    };

    const getStatusBadge = (status: string, type: string) => {
        if (status === "yes") {
            return <Badge bg="success">{type} Eligible</Badge>;
        } else if (status === "possibly") {
            return <Badge bg="warning" text="dark">{type} Possibly</Badge>;
        } else {
            return <Badge bg="secondary">{type} Not Eligible</Badge>;
        }
    };

    const getStatusClassName = (status: string) => {
        if (status === "yes") {
            return "border-success";
        } else if (status === "possibly") {
            return "border-warning";
        } else {
            return "border-secondary";
        }
    };

    return (
        <Card className={`fade-in ${className}`}>
            <FakeLoadingBar isActive={isLoadingDdx} maxDuration={30000} />
            <Card.Body>
                <Row>
                    <Col md={4}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faVideo} fixedWidth /></span><strong>Vertical/Video Eligibility</strong> {isSectionUnread(EXPLAIN_DDX, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={8} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} handleFileVersionChange={handleFileVersionChange} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_DDX} sectionToSetter={sectionToSetter} />
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"vertical_video_eligibility"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isExpanded && ddx && (
                    <>
                        <Row className="mt-2">
                            <Col md={6}>
                                <Card className={`border-2 ${getStatusClassName(ddx.suitable_vertical)}`}>
                                    <Card.Body>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <strong>
                                                <FontAwesomeIcon icon={faPerson} className="me-2" />
                                                Vertical Visit
                                            </strong>
                                            <div>
                                                {getStatusBadge(ddx.suitable_vertical, "Vertical")}
                                                <CopyToClipboardButton
                                                    user={user}
                                                    module={"trackboard_vertical_eligibility"}
                                                    onClick={() => {navigator.clipboard.writeText(getTextToCopy('vertical'))}}
                                                    customStyling="dashboard-round-btn"
                                                    overlayTriggerMessage="Copy Vertical Eligibility"
                                                />
                                            </div>
                                        </div>
                                        <div className="description-text">
                                            {ddx.suitable_vertical_justification ? (
                                                <LLMDashboardMarkdownRenderer
                                                    content={ddx.suitable_vertical_justification}
                                                    scanMulti={scanMulti}
                                                />
                                            ) : (
                                                <span className="text-muted">No justification available</span>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6}>
                                <Card className={`border-2 ${getStatusClassName(ddx.suitable_video)}`}>
                                    <Card.Body>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <strong>
                                                <FontAwesomeIcon icon={faVideo} className="me-2" />
                                                Video Visit
                                            </strong>
                                            <div>
                                                {getStatusBadge(ddx.suitable_video, "Video")}
                                                <CopyToClipboardButton
                                                    user={user}
                                                    module={"trackboard_video_eligibility"}
                                                    onClick={() => {navigator.clipboard.writeText(getTextToCopy('video'))}}
                                                    customStyling="dashboard-round-btn"
                                                    overlayTriggerMessage="Copy Video Eligibility"
                                                />
                                            </div>
                                        </div>
                                        <div className="description-text">
                                            {ddx.suitable_video_justification ? (
                                                <LLMDashboardMarkdownRenderer
                                                    content={ddx.suitable_video_justification}
                                                    scanMulti={scanMulti}
                                                />
                                            ) : (
                                                <span className="text-muted">No justification available</span>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
